import { Phone } from "./components/mobile/phone-shell";
import { JourneyWelcomeScreen } from "./sections/mobile-journey/journey-welcome";
import { JourneyPickSkillScreen } from "./sections/mobile-journey/journey-pick-skill";
import { JourneyProcessingScreen } from "./sections/mobile-journey/journey-processing";
import { JourneyDashboardScreen } from "./sections/mobile-journey/journey-dashboard";
import { JourneyMarketplaceBrowseScreen } from "./sections/mobile-journey/journey-marketplace-browse";
import { JourneySkillDetailScreen } from "./sections/mobile-journey/journey-skill-detail";
import { JourneyInstallDataCheckScreen } from "./sections/mobile-journey/journey-install-data-check";
import { JourneyChatScreen } from "./sections/mobile-journey/journey-chat";

import { FlowSkillsTabWithNew } from "./sections/mobile-flows/flow-skills-tab-new";
import { MeScreen } from "./sections/mobile-screens/me-screen";
import { FlowVaultSearch } from "./sections/mobile-flows/flow-vault-search";
import { FlowFileDetail } from "./sections/mobile-flows/flow-file-detail";
import { FlowBiomarkerDetail } from "./sections/mobile-flows/flow-biomarker-detail";
import { FlowBiomarkerIndex } from "./sections/mobile-flows/flow-biomarker-index";
import { FlowWBCProgression } from "./sections/mobile-flows/flow-wbc-progression";
import {
  FlowHealthDataBrowser,
  FlowStepsDetail,
  FlowMetricPicker,
  FlowGraphExplorerDual,
} from "./sections/mobile-flows/flow-graph-explorer";
import {
  FlowCaptureText,
  FlowCaptureExpanded,
  FlowCaptureSupplement,
  FlowCaptureVoice,
  FlowCapturePhoto,
  FlowEnrichmentProgress,
  FlowHomeWithInsights,
} from "./sections/mobile-flows/flow-capture-data";
import { alpha, borderAlpha } from "./components/mobile/primitives";
import {
  FlowCaptureFood,
  FlowHomeSkillReacts,
  FlowSkillsWithReactions,
  FlowSkillReactionFeed,
  FlowReactionChat,
} from "./sections/mobile-flows/flow-skill-reacts";

// ─── Flow definitions ────────────────────────────────────────────────────────

const FLOWS = [
  {
    id: "skill-setup",
    title: "Get a Skill Running",
    subtitle: "Three ways in, one shared path — every skill follows the same setup flow",
    color: "var(--color-kin-green-soft)",
    trigger:
      "User acquires a skill — through first-launch onboarding, an external marketplace install, or browsing the in-app marketplace.",
    goal: "From any entry point, the user has a working skill dashboard with real analysis results.",
    entryPoints: [
      {
        id: "first-launch",
        title: "First Launch",
        subtitle: "New user downloads and opens the app",
        color: "var(--color-kin-green-mid)",
        steps: [
          {
            num: "1",
            title: "Sign In",
            screen: <JourneyWelcomeScreen />,
            hideTabBar: true,
            desc: "Google SSO only. One tap. No account creation form.",
            must: "Sign-in completes and session is active. Failure shows error + retry.",
          },
          {
            num: "2",
            title: "Choose Your Skill",
            screen: <JourneyPickSkillScreen />,
            hideTabBar: true,
            scrollable: true,
            desc: "Two canonical skills: Health Dashboard and Biomarker Analysis. Pre-selected default. Skip goes straight to home.",
            must: "Selection is persisted. Skip marks onboarding complete and enters main app.",
          },
        ],
      },
      {
        id: "external-install",
        title: "External Install",
        subtitle: "Installed from the web marketplace, then opens the app",
        color: "var(--color-kin-pink)",
        steps: [
          {
            num: "1",
            title: "Skills Tab — New Skill",
            screen: <FlowSkillsTabWithNew />,
            tab: "skills",
            desc: "The marketplace install synced. The skill appears on the Skills tab with a data gap alert if it needs setup.",
            must: "Skill is visible immediately. If data is needed, the attention card shows a 'Set up' action.",
          },
        ],
      },
      {
        id: "in-app-browse",
        title: "In-App Browse",
        subtitle: "Explores the marketplace from within the app",
        color: "var(--color-kin-lavender)",
        steps: [
          {
            num: "1",
            title: "Marketplace Browse",
            screen: <JourneyMarketplaceBrowseScreen />,
            tab: "skills",
            scrollable: true,
            desc: "Search and filter by category. Each skill card shows name, author, tagline, and install count.",
            must: "Skills load from server. Search filters live. Category chips work.",
          },
          {
            num: "2",
            title: "Skill Detail & Install",
            screen: <JourneySkillDetailScreen />,
            tab: "skills",
            scrollable: true,
            desc: "What it analyzes, what data it needs, who created it. 'Install' button at the bottom.",
            must: "Install triggers server-side install. On success, navigate to data check. On error, show message + retry.",
          },
        ],
      },
    ],
    sharedSteps: [
      {
        num: "1",
        title: "Data Check",
        screen: <JourneyInstallDataCheckScreen />,
        tab: "skills",
        scrollable: true,
        desc: "The app audits vault data against what this skill needs. Green checks for met requirements, orange for missing with upload/connect actions. During onboarding this is the vault setup step.",
        must: "Readiness check shows real status. Upload triggers document picker. Health connection works. Continue is available once requirements are met — or user can skip.",
      },
      {
        num: "2",
        title: "Processing",
        screen: <JourneyProcessingScreen />,
        hideTabBar: true,
        desc: "Skill runs with real-time step trace. User sees what the AI is doing: reading files, building dashboard.",
        must: "SSE stream connects and displays steps. On error: retry button + option to skip. On success: auto-navigate to dashboard.",
      },
      {
        num: "3",
        title: "Dashboard",
        screen: <JourneyDashboardScreen />,
        tab: "skills",
        scrollable: true,
        desc: "The payoff. Headline insight, KPIs, trend charts, evidence sections. User can chat with the skill for follow-up.",
        must: "Dashboard renders from skill output. Chat FAB works. Back navigation returns to Skills tab. For first launch, onboarding is marked complete.",
      },
    ],
  },
  {
    id: "analyze-data",
    title: "Analyze My Data",
    subtitle: "User has data in vault → wants to understand it",
    color: "var(--color-kin-orange)",
    trigger: "User has uploaded files or connected health sources and wants to run analysis.",
    goal: "Run a skill against their data and see results — from the Skills tab or from a skill they already have.",
    steps: [
      {
        num: "1",
        title: "Skills Tab",
        screen: <FlowSkillsTabWithNew />,
        tab: "skills",
        desc: "User's installed skills. Each shows its status: ready to run, has results, needs data. Data gap alert if any skill is missing data.",
        must: "Skills load on focus. Category filter works. Tap opens skill-detail. Attention card navigates to data-check.",
      },
      {
        num: "2",
        title: "Skill Detail — Run",
        screen: <JourneySkillDetailScreen />,
        tab: "skills",
        scrollable: true,
        desc: "User sees the skill actions. Taps 'Run' on an action. Inline step trace shows real-time progress.",
        must: "Run button triggers SSE action. Steps display inline. Run completes with success state. Dashboard badge appears.",
      },
      {
        num: "3",
        title: "Dashboard",
        screen: <JourneyDashboardScreen />,
        tab: "skills",
        scrollable: true,
        desc: "The analysis dashboard: headline insight, KPIs, trend charts, evidence sections.",
        must: "Dashboard loads from latest run output. All sections render. Scroll works. Back to skill-detail.",
      },
      {
        num: "4",
        title: "Chat Follow-up",
        screen: <JourneyChatScreen />,
        tab: "skills",
        desc: "User asks questions about the results. Conversation grounded in skill context and vault data.",
        must: "Chat modal opens from FAB. Messages send and receive. Skill context is available. Close returns to skill-detail.",
      },
    ],
  },
  {
    id: "explore-data",
    title: "Explore My Data",
    subtitle:
      "Profile is the hub — search, drill into files, browse biomarkers, or compare metrics",
    color: "var(--color-kin-sky)",
    trigger: "User goes to the Profile tab, taps a file from Home, or taps a metric card.",
    goal: "Navigate from the profile hub to find, understand, and compare any piece of health data.",
    hub: {
      title: "Profile",
      screen: <MeScreen />,
      tab: "profile",
      scrollable: true,
      desc: "The hub. Profile header, search bar, health data highlights, needs-attention card, library rows. Every path starts here.",
      must: "Health data rows show live values and are tappable. Search bar opens vault search. Library rows navigate to filtered file lists. 'See all →' navigates to Health Data Browser. Needs-attention card links to flagged markers.",
    },
    paths: [
      {
        id: "search",
        title: "Search",
        subtitle: "Search bar → filtered results",
        color: "var(--color-kin-sky)",
        steps: [
          {
            num: "1",
            title: "Search Results",
            screen: <FlowVaultSearch />,
            tab: "profile",
            desc: "User taps the search bar on Profile, navigating to All Files. Results filter live as they type.",
            must: "Search input is responsive. Filtering works across titles. Empty state shows helpful message. Clear search restores full list. Back returns to Profile.",
          },
        ],
      },
      {
        id: "file-drill",
        title: "Files & Extracted Values",
        subtitle: "File tap → metadata & biomarkers → individual value detail",
        color: "var(--color-kin-teal)",
        steps: [
          {
            num: "1",
            title: "File Detail",
            screen: <FlowFileDetail />,
            tab: "profile",
            scrollable: true,
            desc: "File metadata (type, category, size, date), image preview if applicable, and extracted biomarker values table.",
            must: "Metadata renders correctly. Biomarker table shows values with flags. Each value row is tappable. Preview and delete buttons work.",
          },
          {
            num: "2",
            title: "Extracted Value",
            screen: <FlowBiomarkerDetail />,
            tab: "profile",
            scrollable: true,
            desc: "Individual biomarker detail: value, unit, reference range, flag status, extraction confidence, raw text snippet, source document link.",
            must: "All fields render. Reference range indicator is visual. Source document link navigates back to file-detail. Flag colors are correct.",
          },
        ],
      },
      {
        id: "biomarker-drill",
        title: "Biomarker Drilldown",
        subtitle: "Biomarker index → category → progression graph & table",
        color: "var(--color-kin-lavender)",
        steps: [
          {
            num: "1",
            title: "Biomarker Index",
            screen: <FlowBiomarkerIndex />,
            tab: "profile",
            scrollable: true,
            desc: "All extracted biomarkers grouped by category — hematology, metabolic, lipids, vitamins, thyroid. Each marker chip shows flag status. Tapping a marker navigates to its progression detail.",
            must: "Index loads from server. Categories render with counts. Flagged markers are visually highlighted. Chips are tappable and navigate to progression.",
          },
          {
            num: "2",
            title: "WBC Progression",
            screen: <FlowWBCProgression />,
            tab: "profile",
            scrollable: true,
            desc: "WBC detail with time-series chart showing 5 data points across 18 months. Reference range band overlaid on chart. History table with date, value, flag, and source lab. Trend note highlights the upward pattern.",
            must: "Chart renders with correct scale and reference band. Data points colored by flag (sage normal, rose high). History table rows are tappable. Trend indicator is accurate.",
          },
        ],
      },
      {
        id: "metric-compare",
        title: "Metrics & Compare",
        subtitle: "Health data browser → metric detail → dual-axis comparison",
        color: "var(--color-kin-orange)",
        steps: [
          {
            num: "1",
            title: "Health Data Browser",
            screen: <FlowHealthDataBrowser />,
            tab: "profile",
            scrollable: true,
            desc: "All health metrics: wearable vitals in a 2-column grid with sparklines, biomarkers by category with flags, index-derived series. Every item is tappable.",
            must: "Back nav returns to Profile. Vitals cards show latest value, trend, and sparkline. Biomarker rows show value, unit, and flag badges. Each row navigates to metric-detail.",
          },
          {
            num: "2",
            title: "Metric Detail — Steps",
            screen: <FlowStepsDetail />,
            hideTabBar: true,
            scrollable: true,
            desc: "Full detail: hero value, trend chart with area fill, time range chips, average and best-day stats. 'Compare with...' action card at the bottom.",
            must: "Chart renders with correct scale. Time range chips update the chart. Stats are accurate. 'Compare with...' is clearly actionable. Back returns to Health Data.",
          },
          {
            num: "3",
            title: "Compare Picker",
            screen: <FlowMetricPicker />,
            hideTabBar: true,
            scrollable: true,
            desc: "Page-sheet modal with all available metrics: Wearables (current metric disabled), Biomarkers by category, Index Series. User selects Homocysteine.",
            must: "Current metric is disabled and labeled 'Current'. Selection triggers fetch. Dismiss returns to metric detail without entering comparison.",
          },
          {
            num: "4",
            title: "Dual-Axis Comparison",
            screen: <FlowGraphExplorerDual />,
            hideTabBar: true,
            scrollable: true,
            desc: "Steps (solid sage, left Y) and Homocysteine (dashed rose, right Y) overlaid with independent scales. Color-coded axis labels, pattern insight, summary stats. Axis pickers allow swapping metrics.",
            must: "Both series render with independent Y-axes. Sparse biomarker data shows larger dots. Legend matches colors. Time range chips filter both. Back returns to Steps detail.",
          },
        ],
      },
    ],
  },
  {
    id: "skill-reacts",
    title: "Skill Gets Pinged",
    subtitle: "User logs data → skill reacts instantly → engagement builds over time",
    color: "var(--color-kin-green-soft)",
    trigger:
      "User logs a meal, syncs a wearable, or uploads a file that an installed skill is watching.",
    goal: "The skill reacts within seconds with a specific, encouraging observation — the user feels heard without running a full analysis.",
    steps: [
      {
        num: "1",
        title: "User Logs a Meal",
        screen: <FlowCaptureFood />,
        tab: "home",
        desc: "User taps + and logs dinner: 'Grilled salmon, brown rice, steamed broccoli.' This is their 5th balanced meal this week. The capture sheet posts to the journal and triggers enrichment.",
        must: "Note saves to journal.jsonl. Enrichment pipeline extracts meal components (protein, carbs, fats). Vault file is created. Any skill with a hook on this data category is notified.",
      },
      {
        num: "2",
        title: "Skill Reacts on Home",
        screen: <FlowHomeSkillReacts />,
        tab: "home",
        desc: "Within seconds, the Nutrition Audit skill reacts. A card appears on the home screen: 'Great protein source — salmon hits your 30g target. This is your 5th balanced dinner in a row.' The reaction is specific to THIS meal and THIS user's history.",
        must: "Reaction appears within 2 seconds of enrichment completing. Card shows which skill reacted and what triggered it. Tapping 'Open' navigates to the skill's detail screen. The reaction uses Haiku (~$0.001) — not a full analysis.",
      },
      {
        num: "3",
        title: "Skills Tab — Activity",
        screen: <FlowSkillsWithReactions />,
        tab: "skills",
        desc: "The Skills tab shows Nutrition Audit as 'Active · 3 reactions today' with the most recent reaction quoted inline. The skill feels alive — it's been watching and commenting all day.",
        must: "Skill card shows reaction count and recency. Latest reaction is visible without tapping into the skill. Badge is green 'Active' — distinct from 'Results ready' or 'New data'. Other skills show their normal states.",
      },
      {
        num: "4",
        title: "Reaction Feed",
        screen: <FlowSkillReactionFeed />,
        tab: "skills",
        scrollable: true,
        desc: "Inside the skill detail: a timeline of reactions grouped by day. Each shows what triggered it, when, and what the skill said. Positive reactions are olive, suggestions are gold, neutral is grey. The feed builds a picture of the skill's ongoing awareness.",
        must: "Feed loads from hook_log.jsonl. Entries are chronological with date headers. Each entry shows trigger context (what the user did) and the skill's reaction. Summary KPIs above (avg protein, streak, balance score) update live.",
      },
      {
        num: "5",
        title: "Chat — Grounded in Reactions",
        screen: <FlowReactionChat />,
        tab: "skills",
        desc: "User opens chat. The skill opens with context from its reactions: 'You've hit 5 balanced dinners in a row.' The conversation is grounded in what the skill has been observing — it's not starting from zero.",
        must: "Chat opens with session divider showing reaction count. Skill references specific meals and reactions from the feed. Quick prompts reflect the skill's current observations. The skill can cross-reference reactions with vault data (labs, supplements) for deeper answers.",
      },
    ],
  },
  {
    id: "capture-data",
    title: "Capture Data → See Insights",
    subtitle: "User captures health data → enrichment runs → insights appear on home",
    color: "var(--color-kin-gold)",
    trigger:
      "User taps the + button on the tab bar to log a note, record a voice memo, or photograph a document.",
    goal: "Capture health data in any format, have the enrichment pipeline process it, and see extracted insights on the home screen within seconds.",
    steps: [
      {
        num: "1",
        title: "Capture — Text Note",
        screen: <FlowCaptureText />,
        tab: "home",
        desc: "User taps + and types a health note. Serif placeholder invites natural language. Five modes along the bottom: Note (active), Voice, Photo, File, Scan. Send button posts to the journal.",
        must: "Text input auto-focuses. Send saves to journal.jsonl via POST /api/capture. Sheet dismisses on success. Note appears in Recent feed immediately.",
      },
      {
        num: "2",
        title: "Capture — Expanded Sheet",
        screen: <FlowCaptureExpanded />,
        tab: "home",
        desc: "User pulls the sheet up to full height. Below the mode selector, a 'Quick Log' section reveals tracker categories: Symptom, Mood, Activity, Food, Supplement, and Measurement. Each row has a colored icon, description, and a 'Log' pill button for one-tap entry.",
        must: "Sheet expands smoothly with spring animation. Tracker rows are tappable. Tapping 'Log' opens a structured input for that tracker type. Scroll is enabled when content overflows.",
      },
      {
        num: "3",
        title: "Tracker — Supplement",
        screen: <FlowCaptureSupplement />,
        tab: "home",
        desc: "User taps the Supplement tile. Their saved stack appears as a checklist — tap a row to mark it as taken. Already-logged items are dimmed with a checkmark. A '2/6' counter tracks progress. 'Log remaining 4' button at the bottom batch-logs everything left.",
        must: "Stack is pre-populated from previous entries. Single tap toggles logged state. Dose chip shown inline (no extra input needed). 'Log remaining' saves all unchecked items in one action. '+ Add supplement' lets users add new items to their stack.",
      },
      {
        num: "4",
        title: "Capture — Voice Recording",
        screen: <FlowCaptureVoice />,
        tab: "home",
        desc: "User switches to Voice mode. Pulsing lavender ring with live timer. Stop button ends recording. Preview shows waveform, playback, caption input, and send.",
        must: "Microphone permission requested on first use. Recording saves as .m4a to vault via upload queue. Journal entry created with type 'voice'. Duration tracked. Caption is optional.",
      },
      {
        num: "5",
        title: "Capture — Photo (Lab Report)",
        screen: <FlowCapturePhoto />,
        tab: "home",
        desc: "User switches to Photo mode, takes a picture of a lab report. Preview shows the image with a caption input. Send uploads to vault and triggers enrichment pipeline.",
        must: "Camera permission requested. Photo saved to vault via copyToVault + enqueueFile. Upload triggers enrichVaultFile → biomarker extraction pipeline. Caption stored as note.",
      },
      {
        num: "6",
        title: "Home — Processing",
        screen: <FlowEnrichmentProgress />,
        tab: "home",
        desc: "Back on the home screen. The uploaded lab report is processing — a card shows 'Analysing · extracting biomarkers...' with a progress bar. Recent feed shows the text note from step 1.",
        must: "Processing card appears while enrichment runs. Progress updates via vault SSE events. Card disappears when enrichment completes. Recent feed is live.",
      },
      {
        num: "7",
        title: "Home — Insights Revealed",
        screen: <FlowHomeWithInsights />,
        tab: "home",
        desc: "Enrichment is complete. 'From Your Data' section shows what was extracted: file summary, flagged biomarkers (LDL high, Triglycerides high), and biomarker index summary. Recent feed shows all captures merged chronologically.",
        must: "From Your Data section appears with enrichment results. Flagged biomarkers show value + unit + flag. File summary shows AI-generated description. Recent feed merges notes, voice, and files sorted by time. Tapping a flagged marker navigates to biomarker detail.",
      },
    ],
  },
];

// ─── Page component ──────────────────────────────────────────────────────────

export default function FlowsPage() {
  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(ellipse at 15% 10%, rgba(255,208,40,0.08) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, rgba(91,122,102,0.07) 0%, transparent 50%), linear-gradient(165deg, #F7F4ED 0%, var(--color-kin-white) 40%, #F2F4EF 70%, #EDF1EC 100%)",
        fontFamily: "'Manrope', system-ui",
      }}
    >
      {/* Top nav */}
      <nav
        className="sticky top-0 z-40 flex items-center gap-4 px-6 sm:px-10 py-3"
        style={{
          background: "rgba(247,245,238,0.70)",
          backdropFilter: "blur(20px) saturate(120%)",
          borderBottom: "1px solid rgba(255,255,255,0.64)",
        }}
      >
        <a
          href="/private/design"
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            textDecoration: "none",
          }}
        >
          ← Brandbook
        </a>
        <span style={{ color: "var(--color-kin-olive-light)" }}>·</span>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-black)",
          }}
        >
          Core Flows
        </p>
      </nav>

      {/* Hero */}
      <header className="px-6 sm:px-10 lg:px-16 pt-14 pb-12 max-w-5xl">
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            marginBottom: 14,
          }}
        >
          08 — Core User Flows
        </p>
        <h1
          style={{
            fontSize: "clamp(32px, 5vw, 56px)",
            fontWeight: 700,
            color: "var(--color-kin-black)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            lineHeight: 1.05,
            marginBottom: 16,
          }}
        >
          The flows that
          <br />
          <span style={{ color: "var(--color-kin-green-soft)" }}>must work.</span>
        </h1>
        <p
          style={{
            fontSize: 15,
            color: "var(--color-kin-muted)",
            lineHeight: 1.7,
            maxWidth: 560,
            fontWeight: 400,
          }}
        >
          Eight core user journeys that define whether the app feels reliable and trustworthy. Each
          flow is a sequence of screens that must connect without dead ends, broken navigation, or
          silent failures.
        </p>

        {/* Flow index */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            marginTop: 28,
          }}
        >
          {FLOWS.map((flow) => (
            <a
              key={flow.id}
              href={`#${flow.id}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "6px 16px",
                borderRadius: 20,
                background: alpha(flow.color, "10"),
                border: borderAlpha(flow.color, "28"),
                textDecoration: "none",
                transition: "all 0.15s ease",
              }}
            >
              <div
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: flow.color,
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: flow.color,
                  fontFamily: "'Manrope', system-ui",
                }}
              >
                {flow.title}
              </span>
            </a>
          ))}
        </div>
      </header>

      {/* Flows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {FLOWS.map((flow) =>
          flow.entryPoints ? (
            <BranchingFlowSection key={flow.id} flow={flow} />
          ) : flow.hub ? (
            <HubFlowSection key={flow.id} flow={flow} />
          ) : (
            <FlowSection key={flow.id} flow={flow} />
          ),
        )}
      </div>

      {/* Footer principles */}
      <footer
        className="px-6 sm:px-10 lg:px-16 py-16"
        style={{ borderTop: "1px solid rgba(108,108,112,0.10)" }}
      >
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <div
            style={{
              padding: "24px 28px",
              background: "rgba(254,253,251,0.55)",
              border: "1px solid rgba(255,255,255,0.42)",
              borderRadius: 24,
              backdropFilter: "blur(20px)",
              boxShadow: "0 8px 32px -12px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)",
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.20em",
                textTransform: "uppercase",
                color: "var(--color-kin-muted)",
                marginBottom: 12,
              }}
            >
              Reliability Principles
            </p>
            <p
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "var(--color-kin-black)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                lineHeight: 1.25,
                marginBottom: 16,
              }}
            >
              Every screen earns the next.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 16,
              }}
            >
              {[
                {
                  title: "No silent failures",
                  body: "Every error has a visible message, a retry option, and a way out. The user never gets stuck.",
                },
                {
                  title: "No dead ends",
                  body: "Every screen has clear forward and backward navigation. Back always goes where the user expects.",
                },
                {
                  title: "No false confidence",
                  body: "If data is missing or processing failed, the UI says so clearly. No green checkmarks on broken states.",
                },
                {
                  title: "Consistent patterns",
                  body: "Same back button style, same loading indicators, same error banners, same card layouts across all screens.",
                },
              ].map((p) => (
                <div key={p.title} style={{ gap: 4 }}>
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--color-kin-black)",
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      marginBottom: 4,
                    }}
                  >
                    {p.title}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      color: "var(--color-kin-muted)",
                      lineHeight: 1.6,
                      fontWeight: 400,
                    }}
                  >
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Branching flow section (skill setup) ───────────────────────────────────

function BranchingFlowSection({ flow }) {
  return (
    <section
      id={flow.id}
      className="px-6 sm:px-10 lg:px-16 py-16"
      style={{ borderBottom: "1px solid rgba(108,108,112,0.10)" }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "5px 14px",
              borderRadius: 20,
              background: alpha(flow.color, "10"),
              border: borderAlpha(flow.color, "28"),
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: flow.color,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: flow.color,
                fontFamily: "'Manrope', system-ui",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Flow
            </span>
          </div>

          <h2
            style={{
              fontSize: "clamp(24px, 3vw, 40px)",
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1.1,
              marginBottom: 8,
            }}
          >
            {flow.title}
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "var(--color-kin-muted)",
              lineHeight: 1.6,
              maxWidth: 520,
              fontWeight: 400,
              marginBottom: 16,
            }}
          >
            {flow.subtitle}
          </p>

          {/* Trigger & Goal */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              maxWidth: 700,
            }}
          >
            <MetaCard label="Trigger" text={flow.trigger} color={flow.color} />
            <MetaCard label="Goal" text={flow.goal} color={flow.color} />
          </div>
        </div>

        {/* ── Entry Points ── */}
        <div style={{ marginBottom: 20 }}>
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--color-kin-muted)",
              fontFamily: "'Manrope', system-ui",
            }}
          >
            Entry Points
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "40px 48px",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          {flow.entryPoints.map((entry) => (
            <EntryPointLane key={entry.id} entry={entry} />
          ))}
        </div>

        {/* ── Convergence divider ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "40px 0 32px",
          }}
        >
          <div
            style={{
              flex: 1,
              height: 1,
              background: `linear-gradient(to right, transparent, ${alpha(flow.color, "30")})`,
            }}
          />
          <div
            style={{
              padding: "5px 18px",
              borderRadius: 20,
              background: alpha(flow.color, "10"),
              border: borderAlpha(flow.color, "28"),
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: flow.color,
                fontFamily: "'Manrope', system-ui",
              }}
            >
              All paths converge ↓
            </span>
          </div>
          <div
            style={{
              flex: 1,
              height: 1,
              background: `linear-gradient(to left, transparent, ${alpha(flow.color, "30")})`,
            }}
          />
        </div>

        {/* ── Shared Steps ── */}
        <div style={{ marginBottom: 20 }}>
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--color-kin-muted)",
              fontFamily: "'Manrope', system-ui",
              marginBottom: 4,
            }}
          >
            Shared Flow
          </p>
          <p
            style={{
              fontSize: 13,
              color: "var(--color-kin-muted)",
              lineHeight: 1.5,
              fontWeight: 400,
              fontFamily: "'Manrope', system-ui",
            }}
          >
            Regardless of entry point, every skill follows these steps.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "40px 32px",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          {flow.sharedSteps.map((step, i) => (
            <FlowStepCard
              key={step.num}
              step={step}
              color={flow.color}
              isLast={i === flow.sharedSteps.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Entry point lane ───────────────────────────────────────────────────────

function EntryPointLane({ entry }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 20,
        flex: "0 0 auto",
      }}
    >
      {/* Lane header */}
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "5px 14px",
            borderRadius: 20,
            background: alpha(entry.color, "10"),
            border: borderAlpha(entry.color, "28"),
            marginBottom: 6,
          }}
        >
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: entry.color,
            }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: entry.color,
              fontFamily: "'Manrope', system-ui",
            }}
          >
            {entry.title}
          </span>
        </div>
        <p
          style={{
            fontSize: 12,
            color: "var(--color-kin-muted)",
            lineHeight: 1.5,
            fontWeight: 400,
            fontFamily: "'Manrope', system-ui",
            maxWidth: 240,
            margin: "0 auto",
          }}
        >
          {entry.subtitle}
        </p>
      </div>

      {/* Steps stacked vertically */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 24,
        }}
      >
        {entry.steps.map((step, i) => (
          <FlowStepCard
            key={step.num}
            step={step}
            color={entry.color}
            isLast={i === entry.steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Hub flow section (explore data) ─────────────────────────────────────────

function HubFlowSection({ flow }) {
  return (
    <section
      id={flow.id}
      className="px-6 sm:px-10 lg:px-16 py-16"
      style={{ borderBottom: "1px solid rgba(108,108,112,0.10)" }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "5px 14px",
              borderRadius: 20,
              background: alpha(flow.color, "10"),
              border: borderAlpha(flow.color, "28"),
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: flow.color,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: flow.color,
                fontFamily: "'Manrope', system-ui",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Flow
            </span>
          </div>

          <h2
            style={{
              fontSize: "clamp(24px, 3vw, 40px)",
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1.1,
              marginBottom: 8,
            }}
          >
            {flow.title}
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "var(--color-kin-muted)",
              lineHeight: 1.6,
              maxWidth: 520,
              fontWeight: 400,
              marginBottom: 16,
            }}
          >
            {flow.subtitle}
          </p>

          {/* Trigger & Goal */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              maxWidth: 700,
            }}
          >
            <MetaCard label="Trigger" text={flow.trigger} color={flow.color} />
            <MetaCard label="Goal" text={flow.goal} color={flow.color} />
          </div>
        </div>

        {/* ── Hub screen ── */}
        <div style={{ marginBottom: 20 }}>
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--color-kin-muted)",
              fontFamily: "'Manrope', system-ui",
            }}
          >
            Hub
          </p>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 0,
          }}
        >
          <FlowStepCard step={{ num: "●", ...flow.hub }} color={flow.color} isLast />
        </div>

        {/* ── Divergence divider ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "36px 0 28px",
          }}
        >
          <div
            style={{
              flex: 1,
              height: 1,
              background: `linear-gradient(to right, transparent, ${alpha(flow.color, "30")})`,
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "5px 18px",
              borderRadius: 20,
              background: alpha(flow.color, "10"),
              border: borderAlpha(flow.color, "28"),
              whiteSpace: "nowrap",
            }}
          >
            {flow.paths.map((p) => (
              <div
                key={p.id}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: p.color,
                }}
              />
            ))}
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: flow.color,
                fontFamily: "'Manrope', system-ui",
                marginLeft: 4,
              }}
            >
              4 paths from profile
            </span>
          </div>
          <div
            style={{
              flex: 1,
              height: 1,
              background: `linear-gradient(to left, transparent, ${alpha(flow.color, "30")})`,
            }}
          />
        </div>

        {/* ── Paths ── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 48,
          }}
        >
          {flow.paths.map((path) => (
            <PathSection key={path.id} path={path} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Path section (horizontal row within hub flow) ──────────────────────────

function PathSection({ path }) {
  return (
    <div>
      {/* Path header */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "5px 14px",
            borderRadius: 20,
            background: alpha(path.color, "10"),
            border: borderAlpha(path.color, "28"),
            marginBottom: 6,
          }}
        >
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: path.color,
            }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: path.color,
              fontFamily: "'Manrope', system-ui",
            }}
          >
            {path.title}
          </span>
        </div>
        <p
          style={{
            fontSize: 12,
            color: "var(--color-kin-muted)",
            lineHeight: 1.5,
            fontWeight: 400,
            fontFamily: "'Manrope', system-ui",
          }}
        >
          {path.subtitle}
        </p>
      </div>

      {/* Steps — horizontal row */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "24px 32px",
          alignItems: "flex-start",
        }}
      >
        {path.steps.map((step, i) => (
          <FlowStepCard
            key={step.num}
            step={step}
            color={path.color}
            isLast={i === path.steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Flow section ────────────────────────────────────────────────────────────

function FlowSection({ flow }) {
  return (
    <section
      id={flow.id}
      className="px-6 sm:px-10 lg:px-16 py-16"
      style={{ borderBottom: "1px solid rgba(108,108,112,0.10)" }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "5px 14px",
              borderRadius: 20,
              background: alpha(flow.color, "10"),
              border: borderAlpha(flow.color, "28"),
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: flow.color,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: flow.color,
                fontFamily: "'Manrope', system-ui",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Flow
            </span>
          </div>

          <h2
            style={{
              fontSize: "clamp(24px, 3vw, 40px)",
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1.1,
              marginBottom: 8,
            }}
          >
            {flow.title}
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "var(--color-kin-muted)",
              lineHeight: 1.6,
              maxWidth: 520,
              fontWeight: 400,
              marginBottom: 16,
            }}
          >
            {flow.subtitle}
          </p>

          {/* Trigger & Goal */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              maxWidth: 700,
            }}
          >
            <MetaCard label="Trigger" text={flow.trigger} color={flow.color} />
            <MetaCard label="Goal" text={flow.goal} color={flow.color} />
          </div>
        </div>

        {/* Steps — horizontal scroll of phone mockups */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "40px 32px",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          {flow.steps.map((step, i) => (
            <FlowStepCard
              key={step.num}
              step={step}
              color={flow.color}
              isLast={i === flow.steps.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Step card (phone + description) ─────────────────────────────────────────

function FlowStepCard({ step, color, isLast }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        width: 270,
        flexShrink: 0,
      }}
    >
      {/* Phone mockup */}
      <Phone active={step.tab || "home"} hideTabBar={step.hideTabBar} scrollable={step.scrollable}>
        {step.screen}
      </Phone>

      {/* Arrow connector (except last) */}
      {!isLast && (
        <div
          style={{
            position: "absolute",
            right: -20,
            top: "40%",
            color: alpha(color, "50"),
            fontSize: 18,
            fontWeight: 300,
            display: "none", // hidden on wrap — visible on wide
          }}
        >
          →
        </div>
      )}

      {/* Description card */}
      <div
        style={{
          width: "100%",
          background: "rgba(254,253,251,0.60)",
          border: "1px solid rgba(255,255,255,0.50)",
          borderRadius: 16,
          padding: "12px 14px",
          backdropFilter: "blur(24px) saturate(150%)",
          boxShadow: "0 4px 20px -8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.7)",
        }}
      >
        {/* Step number + Title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
                fontFamily: "'Manrope', system-ui",
              }}
            >
              {step.num}
            </span>
          </div>
          <p
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1.2,
            }}
          >
            {step.title}
          </p>
        </div>

        {/* Description */}
        <p
          style={{
            fontSize: 12,
            color: "var(--color-kin-muted)",
            lineHeight: 1.6,
            fontFamily: "'Manrope', system-ui",
            fontWeight: 400,
            marginBottom: 8,
          }}
        >
          {step.desc}
        </p>

        {/* Must work */}
        {step.must && (
          <div
            style={{
              padding: "8px 10px",
              borderRadius: 10,
              background: alpha(color, "08"),
              border: borderAlpha(color, "18"),
            }}
          >
            <p
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color,
                marginBottom: 4,
                fontFamily: "'Manrope', system-ui",
              }}
            >
              Must work
            </p>
            <p
              style={{
                fontSize: 11,
                color: "var(--color-kin-black)",
                lineHeight: 1.5,
                fontFamily: "'Manrope', system-ui",
                fontWeight: 400,
              }}
            >
              {step.must}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Meta card (trigger / goal) ──────────────────────────────────────────────

function MetaCard({ label, text, color }) {
  return (
    <div
      style={{
        flex: "1 1 280px",
        padding: "12px 16px",
        borderRadius: 14,
        background: "rgba(254,253,251,0.50)",
        border: "1px solid rgba(255,255,255,0.42)",
        backdropFilter: "blur(16px)",
      }}
    >
      <p
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color,
          marginBottom: 6,
          fontFamily: "'Manrope', system-ui",
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: 13,
          color: "var(--color-kin-black)",
          lineHeight: 1.5,
          fontWeight: 400,
          fontFamily: "'Manrope', system-ui",
        }}
      >
        {text}
      </p>
    </div>
  );
}
